import type { Me, NeedsAttention } from "../shared/api/types";

const SLA_KINDS = new Set(["sla_at_risk", "sla_breached"]);
const WORKLOG_KINDS = new Set(["worklogs_rejected", "worklogs_conflict"]);

interface AttentionAudience {
  modes: Me["modes"];
  flag(name: string): boolean;
}

/** Visible Needs attention lines: SLA lines vanish in Mode A or with notices off; worklog lines while log time is off (S1-AC2). */
export function attentionItems(data: NeedsAttention | undefined, audience: AttentionAudience): NeedsAttention["items"] {
  return (data?.items ?? [])
    .filter((item) => !(SLA_KINDS.has(item.kind) && (audience.modes.slaHidden || !audience.flag("SlaNotifications"))))
    .filter((item) => !(WORKLOG_KINDS.has(item.kind) && audience.modes.logTime === "copy_id_prompt"))
    // Batch 17: an emergency code line only while Features__EmergencyOtp is on (the server omits it too).
    .filter((item) => !(item.kind === "emergency_codes_pending" && !audience.flag("EmergencyOtp")));
}

export function attentionCount(data: NeedsAttention | undefined, audience: AttentionAudience): number {
  return attentionItems(data, audience).reduce((sum, item) => sum + item.count, 0);
}

/**
 * "What's on today" (UX-01 4.12, FLAG-AC2): every flag in words. Every flag the server defines
 * (`Giraffe.Application.Platform.Features.FeatureFlag`) has a label here, checked by flags.test.ts against
 * shared/web-crosscheck/reference.json, whose `flags` carry the same words (test round U1 D-U1-05:
 * `ServiceDeskActions` and `AccessTokens` had none and showed as code names). A flag the server adds later without a
 * label here is spelled out from its name ("AccessTokens" → "Access tokens") rather than shown as a code name.
 */
export const FLAG_LABELS: Record<string, string> = {
  Sprints: "Sprints (backlog and active sprint)",
  KanbanBoard: "Kanban board",
  ServiceDeskQueue: "Service desk queue",
  SlaNotifications: "SLA at-risk and breach notices",
  SlaTimersHidden: "SLA timers hidden (degraded mode A)",
  DevOpsQueueUi: "DevOps queue screen",
  ApprovalsQueueUi: "Approvals queue screen",
  ProdDeploymentMoratorium: "Production deployment moratorium",
  EmailNotifications: "Email notifications",
  WorklogSync: "Log time in Giraffe",
  PartnerAccess: "Partner access",
  AttachmentLazyBackfill: "Attachments still copying from Jira",
  Mentions: "@mentions in comments",
  Estimation: "Estimation breakdown",
  DeploymentPlan: "Deployment plans",
  PmoApi: "PMO Console API",
  McpRead: "MCP read tools",
  McpWrite: "MCP write tools",
  McpDevOps: "MCP DevOps tools",
  Retention: "Retention runs",
  RoleTestMode: "Test mode for System Admins (View as)",
  GuidedTours: "Guided tours and what's new",
  PinFeedback: "Pin feedback (Feedback button)",
  PermissionsPage: "Settings: Permissions page",
  TimerMoves: "Timed workflow moves (auto-close after working days)",
  ServiceDeskActions: "Service desk actions (pause, resume and reply to the requester)",
  AccessTokens: "Personal access tokens",
  TelegramNotifications: "Telegram notifications",
  NotificationPanel: "Notification bell panel and item Watch",
  PersonCard: "Person card on hover and avatars in item lists",
  ProjectIcons: "Project icons and the project picker",
  PhoneSearch: "Phone search button",
  MoveReasons: "Moves you cannot make, with who can",
  SelfReview: "Self-review with a reason (Complete Review)",
  SummaryValidation: "Summary and description check",
  SprintPlannedStart: "Sprint planned start and two-week defaults",
  SprintRetrospective: "Sprint retrospective",
  ProjectReports: "Project reports",
  QuickFilterBuilder: "Quick filter builder for PM and PD",
  NotificationSettings: "Notification settings page",
  EmergencyOtp: "Emergency OTP bypass",
  Passkeys: "Passkey sign-in",
  ListPreview: "Item preview panel on the item lists",
  Events: "Events and test rounds",
  ExternalPortal: "External tester portal",
  ProjectStatus: "Weekly project status",
  Wiki: "Wiki",
  Presentations: "Presentations",
};

/** "AccessTokens" → "Access tokens", "DevOpsQueueUi" → "Dev ops queue ui": a readable fallback for a flag with no label yet. */
export function flagWords(name: string): string {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(/[\s_]+/)
    .filter(Boolean);
  return words.map((word, i) => (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : /^[A-Z0-9]+$/.test(word) ? word : word.toLowerCase())).join(" ");
}

export interface FlagLine {
  name: string;
  label: string;
  on: boolean;
}

export function flagLines(flags: Record<string, boolean>): FlagLine[] {
  const names = new Set([...Object.keys(FLAG_LABELS), ...Object.keys(flags)]);
  return [...names].map((name) => ({ name, label: FLAG_LABELS[name] ?? flagWords(name), on: flags[name] === true }));
}

export function logTimeWords(mode: Me["modes"]["logTime"]): string {
  switch (mode) {
    case "copy_id_prompt":
      return "Log time: off in Giraffe. Log hours in the timesheet against the item's global ID.";
    case "hold":
      return "Log time: saved in Giraffe only, sent to the timesheet later.";
    case "live":
      return "Log time: sent to the timesheet.";
  }
}
